/* eslint lit-a11y/click-events-have-key-events: 0 */
import type { PropertyDeclarations } from 'lit';

import type { KnowledgeBot } from '../core/knowledge_bot';
import type { IMessage, t_feedback_type } from '../types/network';
import type { Ref } from 'lit/directives/ref';

import { nothing, html, LitElement } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { repeat as repeatLit } from 'lit/directives/repeat.js';

import dom from '@eab-pip/pi-dom';
import f1 from '@eab-pip/pi-f1';

import { NavKnowledgeBotMixin } from '../mixins/kbi_mixin';
import { style as styleInterface } from '../styling/interface';
import { style as styleMarkdown } from '../styling/markdown';
import { IconDB } from '../styling/icon_db';

/**
 * an interface for a KnowledgeBot
 *
 * @customElement
 * @polymer
 * @element eab-nav-knowledge-bot
 *
 */
export class NavKnowledgeBotElement extends NavKnowledgeBotMixin(LitElement) {
  static readonly is = 'eab-nav-knowledge-bot';
  static readonly styles = [styleInterface, styleMarkdown];

  /**
   * the `talk-panel-footer-anchor` element at the bottom of all messages
   */
  readonly talkPanelFooterAnchorRef: Ref<HTMLDivElement> = createRef();

  /**
   * the `talk-panel-header-anchor` element at the top of all messages
   */
  readonly talkPanelHeaderAnchorRef: Ref<HTMLDivElement> = createRef();

  /**
   * the `talk-panel-header-anchor` element at the top of all messages
   */
  readonly footerActionsRef: Ref<HTMLDivElement> = createRef();

  /**
   * the `talk-textarea` element where the human types
   */
  readonly talkInputRef: Ref<HTMLTextAreaElement> = createRef();

  /**
   * track is scrolling is active so we do not overload the `scrollIntoView` method and halt scrolling
   */
  private __scrolling = false;

  /**
   * show a loading indicator between send-and-wait
   */
  private __loading = false;

  /**
   * to store recent message feedback token to display thankyou feedback note
   */
  private __recentToken: string;

  /**
   * when locking the interface if the network fails and never updates, then we need to
   * unlock the interface
   */
  private __resetLockInterval: number;

  /**
   * a flag to track if the interface is locked
   */
  locked = false;

  static get properties(): PropertyDeclarations {
    return {
      locked: {
        type: Boolean,
        attribute: true,
        reflect: true
      }
    };
  }

  /**
   * listen for message added or amended because then we must update the interface
   */
  constructor() {
    super();
    this.__stopTalking();
  }

  /**
   * shutdown speech synthesis as an failsafe
   */
  private __stopTalking() {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  /**
   *
   */
  connectedCallback() {
    super.connectedCallback();

    this.bot.on('message-amended', (message: IMessage) => {
      this.scrollDownEffect();
      this.requestUpdate();
      return message;
    });

    this.bot.on('message-added', (message: IMessage) => {
      this.scrollDownEffect();
      this.requestUpdate();
      return message;
    });

    this.bot.on('message-done', (message: IMessage) => {
      this.__loading = false;
      this.requestUpdate();

      this.lock(false);
      return message;
    });

    this.bot.on('message-failed', (text?: string) => {
      this.bot.messaging.writeMessage(text ?? '', 'to-human');
      this.lock(false);
    });

    // if the bot resets, then we need to reset the interface
    this.bot.pointCut.add('after', 'reset', () => {
      this._resetInterface();
    });
  }

  /**
   * shut the bot down, which will abort/stop network connections
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.__stopTalking();

    if (this.__resetLockInterval) {
      clearTimeout(this.__resetLockInterval);
    }
  }

  /**
   * after first render, listen for clicks on the bot
   */
  firstUpdated() {
    // wait until shadowRoot is created
    // links inside of the message bubble up to the shadowRoot
    //
    this.shadowRoot?.addEventListener('click', (evt: CustomEvent) => {
      // play link click events on the bot element
      //
      this.dispatchEvent(
        new CustomEvent('link-click-ended', {
          composed: true,
          bubbles: true,
          detail: evt
        })
      );
    });

    this.__reflectFooterActions();
    this.footerActionsRef.value?.addEventListener('slotchange', () => {
      this.__reflectFooterActions();
    });
  }

  private __reflectFooterActions() {
    const footerActionsEl = this.footerActionsRef.value;
    const slotButtons = Array.from(this.children).filter((el) => el.getAttribute('slot') === 'slot-action-buttons');
    const hasFooterActions = slotButtons.length ?? 0 > 0;

    footerActionsEl?.parentElement?.classList.toggle('empty-slot', !hasFooterActions);
  }

  update(props: Map<string, unknown>) {
    super.update(props);

    if (props.has('messages')) {
      this.bot.clearMessages();
      this.bot.insertMessages(this.messages);
    }
  }

  /**
   * render the header, list of messages, and text input footer
   */
  render() {
    const messages = this.bot.messages.readAll();
    const settings = (this.settings = f1.merge(
      'over',
      f1.merge('over', {}, this.settingsDefaults),
      f1.safe({}, this.settings)
    ));

    const classes = [
      'talk-wrapper',
      settings.hidden.header ? 'hidden-header' : '',
      settings.hidden.footer ? 'footer-header' : ''
    ]
      .join(' ')
      .trim();

    return html` <aside lang="en" class=${classes}>
      ${settings.hidden.header ? nothing : this.__renderHeader()}
      <section
        class="talk-panel"
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions"
        tabindex="0"
        @click=${this.__messageList__onClick__button}
        @value-notified=${this.__messageList__onValueNotified__button}
      >
        <div class="talk-panel-header-anchor" role="presentation" ${ref(this.talkPanelHeaderAnchorRef)}></div>
        <ul>
          ${repeatLit(
            messages,
            (_: IMessage) => _.id,
            (msg: IMessage, index) => {
              const messageType = msg.type;
              const toBot = 'to-bot' === messageType;
              const fromBot = 'from-bot' === messageType;
              const hasContent = !!msg.content;
              const hasTail = (fromBot || toBot) && hasContent;

              return html`
                <li class="message-type--${msg.type} ${hasTail ? 'has-tail' : ''}">
                  ${this.__renderMessage(msg, index)}
                </li>
              `;
            }
          )}
        </ul>
        ${settings.hidden.loading_indicator ? nothing : this.__renderLoading()}
        <div class="talk-panel-footer-anchor" role="presentation" ${ref(this.talkPanelFooterAnchorRef)}></div>
      </section>
      ${settings.hidden.footer || this.readOnly ? nothing : this.__renderFooter()}
    </aside>`;
  }

  /**
   * (un)lock the interface so the user cannot send messages
   */
  lock(locked: boolean) {
    if (!this.settings.allow.lock_on_send) {
      return this;
    }

    this.locked = locked;

    if (!locked) {
      this.refocus();
    }

    if (this.__resetLockInterval) {
      clearInterval(this.__resetLockInterval);
    }

    if (locked) {
      this.__resetLockInterval = setInterval(() => {
        if (!this.bot.isMsgStreaming()) {
          this.lock(false);
        }
      }, 3000);
    }

    return this;
  }

  /**
   * place the focus on the textarea
   * @return resolve if focus and reject if textarea not found
   */
  refocus() {
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        if (this.talkInputRef.value) {
          this.talkInputRef.value.focus();
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  /**
   * erase the messages and reset the network connection
   */
  reset() {
    // clear the bot and create a new network
    this.bot.reset();
    return this;
  }

  /**
   * reset the interface in response to or with a bot reset
   */
  protected _resetInterface() {
    this._displayInitialMessage();
    this.clearUserInput();
    this.lock(false);

    this.requestUpdate();
  }

  /**
   * read the text from the user (in the textarea)
   */
  readUserInput() {
    const inputEl = this.talkInputRef.value;
    if (!inputEl) {
      return '';
    }

    return !f1.is.empty(inputEl.value.trim()) ? inputEl.value : '';
  }

  /**
   * clear the user textarea
   */
  clearUserInput() {
    const inputEl = this.talkInputRef.value;
    if (inputEl) {
      inputEl.value = '';
    }
  }

  /**
   * send text to the user and show a load-waiting indicator
   */
  async sendMessageToBot() {
    const input = this.readUserInput();
    if (f1.is.empty(input)) {
      return;
    }

    try {
      this.__loading = true;
      const detail = await this.bot.pushTo(input);
      this.__loading = false;

      this.dispatchEvent(
        new CustomEvent('send-to-bot-ended', {
          composed: true,
          bubbles: true,
          detail
        })
      );
    } finally {
      this.clearUserInput();
    }
  }

  /**
   * scroll the conversations panel down if we are not currently scrolling
   */
  scrollDownEffect() {
    if (this.__scrolling) {
      return;
    }

    // find the footer so we can scroll to it, which is the end of the bot
    //
    const talkPanelFooterEl = this.talkPanelFooterAnchorRef.value;
    if (!talkPanelFooterEl) {
      return;
    }

    this.__scrolling = true;

    setTimeout(() => {
      this.__scrolling = false;

      talkPanelFooterEl.scrollIntoView({
        behavior: 'smooth',
        inline: 'end',
        block: 'end'
      });
    }, 256);
  }

  /**
   * render the knowledge bot header
   */
  private __renderHeader() {
    const headingClose = this.settings.hidden.heading_close;

    return html`<header class="talk-header">
      <h2 id="bot-header-title">${this.heading ?? this.title}</h2>
      ${headingClose
        ? nothing
        : html`<button
            class="icon has-tip to-left"
            aria-label="Close Knowledge Bot"
            @click=${this.__closeHeader__onClick}
          >
            ${IconDB.close}
            <span class="tip-text">Close</span>
          </button>`}
    </header>`;
  }

  /**
   * render the loading image while we wait
   */
  private __renderLoading() {
    if (!this.__loading) {
      return nothing;
    }

    return html` <div class="talk-message" role="presentation">
      <div class="loading-container">
        <div class="container">
          <div class="loading-container__loader" aria-label="loading"></div>
        </div>
        <p class="loading-container__message">${this.settings.labels.loading_text}</p>
      </div>
    </div>`;
  }

  /**
   * render the knowledge bot footer
   */
  private __renderFooter() {
    const labels = this.settings.labels;
    const isMessageStreaming = this.bot.isMsgStreaming();
    const placeholderTxt = isMessageStreaming ? labels.textarea_streaming_placeholder : labels.textarea_placeholder;
    const settings = this.settings;
    const isLocked = settings.allow.lock_on_send && this.locked;
    const rawTextInput = settings.allow.raw_text_input;

    return html` <footer class="action-buttons">
        <slot class="slot-action-buttons" ${ref(this.footerActionsRef)} name="slot-action-buttons"></slot>
      </footer>
      <footer class="talk-footer">
        <form class="talk-input">
          ${this.settings.hidden.reset
            ? nothing
            : html` <button
                class="icon has-tip to-right"
                aria-label=${labels.tip_button_refresh}
                ?disabled=${isLocked}
                @click=${this.__talkReset__onTriggerEnded}
              >
                ${IconDB.refresh}
                <span class="tip-text">${labels.tip_button_refresh}</span>
              </button>`}
          <textarea
            id="input"
            ${ref(this.talkInputRef)}
            rows="1"
            cols="20"
            wrap="off"
            minlength="0"
            autocomplete="off"
            maxlength=${settings.security.max_input_length}
            spellcheck=${rawTextInput ? 'false' : 'true'}
            autocapitalize=${rawTextInput ? 'off' : 'on'}
            autocorrect=${rawTextInput ? 'off' : 'on'}
            .placeholder=${placeholderTxt}
            ?disabled=${isLocked}
            @keydown=${this.__talkInput__onKeyDown}
          ></textarea>
          <button
            class="icon has-tip to-left"
            aria-label=${labels.tip_button_submit}
            aria-describedby="talk-panel"
            ?disabled=${isLocked}
            @click=${this.__talkSubmit__onTriggerEnded}
          >
            ${isLocked ? IconDB.lock : IconDB.send}
            <span class="tip-text">${labels.tip_button_submit}</span>
          </button>
        </form>
      </footer>`;
  }

  /**
   * render the knowledge bot response and user feedback buttons
   */
  private __renderMessage(message: IMessage, index: number) {
    const messageType = message.type;
    const isMessageDone = messageType === 'message-done';

    return isMessageDone
      ? this.settings.allow.user_feedback
        ? this.__renderFeedback(message, index)
        : nothing
      : this.__renderMessageBlock(message);
  }

  /**
   * render a response message tile
   */
  private __renderMessageBlock(message: IMessage) {
    return this.bot.pointCut.render(
      (msg: IMessage) => this.__renderArticle(msg),
      message,
      'render-message-block',
      (msg: IMessage, template) => {
        return template(msg);
      }
    );
  }

  /**
   * render a single article response or request
   */
  private __renderArticle(msg: IMessage) {
    if (!msg.content) {
      return nothing;
    }

    const messageType = msg.type;
    const toBot = 'to-bot' === messageType;
    const warningMsg = 'to-human' === messageType;
    const isMessageIn = 'from-bot' === messageType;
    const willShowTimestamp = toBot && !this.settings.hidden.timestamp && this.readOnly;

    return html`
      <article class="talk-message talk-message--${messageType}">
        ${this.image && isMessageIn
          ? html` <img class="profile-avatar" src=${this.image} alt=${this.settings.labels.avatar_a11y_text} />`
          : warningMsg
          ? html`${IconDB.warning}`
          : nothing}
        <div class="text-md tile">${unsafeHTML(msg.content)}</div>
      </article>
      ${willShowTimestamp ? html`<span class="message-timestamp">${msg.timestamp}</span>` : nothing}
    `;
  }

  /**
   * render the feedback buttons thumbs up and thumbs down
   */
  private __renderFeedback(message: IMessage, index: number) {
    const { token, feedback, feedbackComments } = message;
    if (!token) {
      return nothing;
    }

    const hasNoFeedback = f1.is.nil(feedback);
    const isFeedback = f1.to.boolean(feedback);
    const labels = this.settings.labels;
    const dislikeFeedbackLabel = labels.tip_message_button_dislike ?? labels.tip_feedback_button_dislike;

    return html`
      <div class="talk-message feedback-controls">
        ${this.__renderMessageControls(message, index)}
        <button
          class="icon has-tip to-right"
          name="dislike"
          aria-label=${dislikeFeedbackLabel}
          ?disabled=${this.bot.isMsgStreaming()}
          @click=${() => this.__messageFeedbackButton__onTriggerEnded('dislike', token, index)}
        >
          ${hasNoFeedback ? IconDB.dislike_outline : isFeedback ? IconDB.dislike_outline : IconDB.dislike_fill}
          <span class="tip-text">${dislikeFeedbackLabel}</span>
        </button>
      </div>
      ${this.__recentToken === token ? this.__renderThankYouMessage() : nothing}
      ${feedbackComments ? this.__renderFeedbackSurvey(token, index) : nothing}
    `;
  }

  private __renderMessageControls(message: IMessage, index: number) {
    const labels = this.settings.labels;

    return html`
      <button
        class="icon has-tip to-right"
        name="copy"
        aria-label=${labels.tip_message_button_copy}
        ?disabled=${this.bot.isMsgStreaming()}
        @click=${() => this.__messageCopyButton__onTriggerEnded(message, index)}
      >
        ${IconDB.copy}
        <span class="tip-text">${labels.tip_message_button_copy}</span>
      </button>
      <button
        class="icon has-tip to-right"
        name="speak"
        aria-label=${labels.tip_message_button_speak}
        ?disabled=${this.bot.isMsgStreaming()}
        @click=${() => this.__messageSpeakButton__onTriggerEnded(message, index)}
      >
        ${IconDB.speak}
        <span class="tip-text">${labels.tip_message_button_speak}</span>
      </button>
    `;
  }

  private __renderThankYouMessage() {
    return html`
      <article class="talk-message talk-message--from-bot">
        ${this.image
          ? html`<img class="profile-avatar" src=${this.image} alt=${this.settings.labels.avatar_a11y_text} />`
          : nothing}
        <div class="text-md tile">
          <p>${this.settings.labels.feedback_acknowledgement}</p>
        </div>
      </article>
    `;
  }

  /**
   * render the user feedback survey form
   */
  private __renderFeedbackSurvey(token: string, index: number) {
    const labels = this.settings.labels;

    return html`
      <div class="feedback-survey-container">
        <div class="feedback-survey-label">
          <p>${labels.feedback_question_prompt}</p>
          <button
            class="icon close has-tip to-left"
            aria-label=${labels.tip_feedback_button_close}
            @click=${(e: KeyboardEvent) => this.__closeFeedback__onTriggerEnded(e, index)}
          >
            ${IconDB.close}
            <span class="tip-text">${labels.tip_feedback_button_close}</span>
          </button>
        </div>
        <form class="feedback-survey-form">
          <textarea
            id="feedback-${token}"
            autocomplete="off"
            minlength="0"
            wrap="soft"
            rows="2"
            cols="20"
            placeholder=${labels.feedback_textarea_placeholder}
            @keydown=${(e: KeyboardEvent) => this.__feedbackInput__onKeyDown(e, token, index)}
          ></textarea>
          <button
            type="submit"
            class="action"
            aria-label=${labels.feedback_button_submit}
            @click=${(e: KeyboardEvent) => this.__feedbackSubmit__onTriggerEnded(e, token, index)}
          >
            ${labels.feedback_button_submit}
          </button>
        </form>
      </div>
    `;
  }

  /**
   * when the user presses ENTER in the textarea, then we should send to the bot
   *
   * shift+enter will allow a new line
   * enter will submit
   */
  private __talkInput__onKeyDown(evt: KeyboardEvent) {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      this.__sendInputAsMessage();
    }
  }

  private __speechButtonOn__onClick(evt: MouseEvent) {
    this.speechOn();
  }

  private __speechButtonOff__onClick(evt: MouseEvent) {
    this.speechOff();
  }

  speechOn() {
    console.dir('deprecated: only read a single message');
  }

  speechOff() {
    console.dir('deprecated: only read a single message');
  }

  /**
   * close the knowledge bot header
   */
  private __closeHeader__onClick(evt: MouseEvent) {
    this.remove();
  }

  /**
   * when the user presses the talk-submit button, then we should send to the bot
   */
  private __talkSubmit__onTriggerEnded(evt: CustomEvent) {
    evt.preventDefault();

    const input = this.readUserInput();
    if (f1.is.empty(input)) {
      return;
    }

    this.__sendInputAsMessage();
  }

  /**
   * start network if stopped and send input as message
   * @note do not send anything if we are streaming a message
   * @note if the network has not started then we have to wait to send messages
   */
  private async __sendInputAsMessage() {
    const isMessageStreaming = this.bot.isMsgStreaming();
    if (isMessageStreaming) {
      return;
    }

    this.lock(true);

    try {
      await this.__startNetwork();
      this.sendMessageToBot();
      this.clearUserInput();
    } catch {
      this.__informNetworkState();
      this.lock(false);
    }
  }

  private __startNetwork() {
    return this.bot.startup().then(() => {
      this.__informNetworkState();
    });
  }

  private __informNetworkState() {
    const networkState = this.bot.network.getState();
    if (networkState === 'closing' || networkState === 'closed') {
      this.bot.messaging.writeError('Something went wrong. Please try again.');
    }
  }

  /**
   * when the user presses the talk-clear button we clear all messages
   */
  private __talkReset__onTriggerEnded(evt: CustomEvent) {
    evt.preventDefault();
    this.reset();
  }

  /**
   *
   */
  private async __messageCopyButton__onTriggerEnded(message: IMessage, messageIndex: number) {
    this._copyMessage(message);
  }

  protected _copyMessage(message: IMessage) {
    const content = f1.read('/lastMessage/raw', null, message);
    if (!content) {
      return;
    }

    const divEl = document.createElement('div');

    try {
      divEl.innerHTML = content;

      return navigator.clipboard.writeText(divEl.textContent ?? content);
    } catch (error) {
      return Promise.reject(`no content`);
    }
  }

  /**
   *
   */
  private __messageSpeakButton__onTriggerEnded(message: IMessage, messageIndex: number) {
    this._speakMessage(message);
  }

  protected _speakMessage(message: IMessage) {
    if (!('speechSynthesis' in window)) {
      return;
    }

    const content = f1.read('/lastMessage/raw', null, message);
    if (!content) {
      return;
    }

    if (window.speechSynthesis.speaking) {
      return window.speechSynthesis.cancel();
    }

    try {
      const utterance = new SpeechSynthesisUtterance(content);
      const voices = window.speechSynthesis.getVoices();

      // @note british sounding synthetic voices sound the most human
      utterance.voice = voices.find(({ lang }) => lang === 'en-GB') ?? voices[0];
      utterance.lang = 'en-US';
      utterance.text = content;
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.volume = 1;
      utterance.volume = 0.8;

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
      window.speechSynthesis.resume();
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * when the user presses the like or dislike button, then we should send to the bot
   */
  private async __messageFeedbackButton__onTriggerEnded(
    response: t_feedback_type,
    token: string,
    messageIndex: number
  ) {
    this.__recentToken = '';

    const hasDuplicates = await this.bot.feedback.sendFeedback(response, token, messageIndex);
    if (hasDuplicates) {
      this.bot.feedback.additionalSurvey(messageIndex, false);
    }
  }

  /**
   * when the user presses the close button of feedback survey form
   */
  private __closeFeedback__onTriggerEnded(evt: KeyboardEvent, messageIndex: number) {
    this.__recentToken = '';
    this.bot.feedback.additionalSurvey(messageIndex, false);
  }

  /**
   * when the user enters the feedback comments in the textarea and presses ENTER
   */
  private __feedbackInput__onKeyDown(evt: KeyboardEvent, token: string, messageIndex: number) {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      evt.preventDefault();
      this.__sendAdditionalFeedback(evt, token, messageIndex);
    }
  }

  /**
   * when the user presses the submit button of feedback survey form, then we should send to the bot
   */
  private __feedbackSubmit__onTriggerEnded(evt: KeyboardEvent, token: string, messageIndex: number) {
    evt.preventDefault();
    this.__sendAdditionalFeedback(evt, token, messageIndex);
  }

  /**
   * find the textarea on the event and send the content value to the API
   */
  private __sendAdditionalFeedback(evt: KeyboardEvent, token: string, messageIndex: number) {
    const evtEl = evt.currentTarget as HTMLButtonElement;
    const textareaEl = evtEl.localName === 'textarea' ? evtEl : (evtEl.previousElementSibling as HTMLButtonElement);
    const text = textareaEl.value.trim();
    if (!text) {
      return;
    }

    const feedbackMessage = this.bot.messages.findMessage(messageIndex);
    const feedback = !!feedbackMessage;

    // send user feedback
    this.bot.feedback.postUserFeedback({ token, text, feedback }, messageIndex);
    this.bot.feedback.additionalSurvey(messageIndex, false);

    // remember the token from the last message
    this.__recentToken = token;
  }

  /**
   * if a dynamic button is clicked in a message then if it has a method it will execute
   * that on the bot
   */
  private __messageList__onClick__button(evt: CustomEvent) {
    const clickedEl = evt.target as HTMLElement | null;
    if (!clickedEl) {
      return;
    }

    if (clickedEl.localName !== 'button') {
      return;
    }

    return this.__invokeButtonMethod(clickedEl);
  }

  /**
   * if a dynamic button is clicked in a message then if it has a method it will execute
   * that on the bot
   */
  private __messageList__onValueNotified__button(evt: CustomEvent) {
    return this.__invokeButtonMethod(evt.detail.srcElement);
  }

  private __invokeButtonMethod(buttonEl: HTMLElement) {
    const method = buttonEl.getAttribute('data-method') as null | keyof KnowledgeBot;
    if (f1.is.nil(method)) {
      return;
    }

    const botFn = this.bot[method] as unknown as Function;
    if (!f1.is.function(botFn)) {
      return;
    }

    const _parameter: string | null = buttonEl.getAttribute('data-parameter') ?? null;
    const _parameters: string = buttonEl.getAttribute('data-parameters') ?? '[]';
    const parameters = f1.compact(f1.push(_parameter, f1.parse('json', {}, _parameters)));

    botFn.apply(this.bot, parameters);
  }
}

dom.registerElement(NavKnowledgeBotElement);
